// /payments/reconciliation — Razorpay Payment Reconciliation Dashboard — G1.2
//
// MANAGER+. Aggregates captured / refunded / variance across a date range,
// broken down by day and payment method. Data is driven by the server-side
// /api/v1/payments/reconciliation/* endpoints.
// Traceability: FR-BILL-048, FR-RPT-005, NFR-039.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer, Button, Skeleton, ErrorBoundary, useToast } from '@bbpos/ui';
import { paymentsService } from '@/services/payments.service';
import { offlinePaymentService } from '@/services/offline-payment.service';
import { ApiError } from '@/lib/api-client';
import { GLASS, cn } from '@/constants/design-tokens';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { ReconciliationSummary, VarianceRow, GatewaySettlement, CurrencyCode } from '@bbpos/types';

export function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  // Use local calendar parts (not toISOString(), which is UTC and would shift
  // the date a day early in IST during the 00:00–05:30 window).
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Delegates to the shared, currency-aware formatter. The INR path is
// byte-identical to the previous inline rupee/100 toLocaleString (min:2,max:2).
function formatRupees(paise: number, currency: CurrencyCode): string {
  return formatPaise(paise, currency);
}

// Builds the reconciliation CSV. Amounts are stored as integer minor units
// (paise for INR, baisa for OMR, etc.), so the export must scale them to major
// units using the tenant currency's subunit factor and label them neutrally —
// the old `captured_paise` / `refunded_paise` headers were India-specific and
// wrong for non-INR tenants (issue #257). A trailing `currency` column makes the
// unit unambiguous. Values use a plain (non-grouped) fixed-decimal string so no
// thousands separators leak commas into CSV cells.
//
// The variance rows and the settlements table follow as their own blocks
// (blank line + header) so the file carries what the operator reconciles
// against — every variance row, not just the ten shown on screen.
export function buildReconciliationCsv(
  daily: ReconciliationSummary['daily'],
  currency: CurrencyCode,
  variance: VarianceRow[] = [],
  settlements: GatewaySettlement[] = [],
): string {
  const multiplier = getSubunitMultiplier(currency);
  const decimals = Math.round(Math.log10(multiplier));
  const toMajor = (minor: number): string => (minor / multiplier).toFixed(decimals);
  const cell = (v: string): string => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = ['date,captured_amount,refunded_amount,bill_count,currency'];
  daily.forEach((r) =>
    lines.push(`${r.date},${toMajor(r.captured)},${toMajor(r.refunded)},${r.bill_count},${currency}`),
  );
  if (variance.length > 0) {
    lines.push('', 'bill_number,total_amount,captured_amount,variance_amount,type,currency');
    variance.forEach((v) =>
      lines.push(
        `${cell(v.bill_number)},${toMajor(v.grand_total)},${toMajor(v.captured)},${toMajor(v.variance)},${v.type},${currency}`,
      ),
    );
  }
  if (settlements.length > 0) {
    lines.push('', 'settlement_id,utr,amount,fees,tax,status,date,currency');
    settlements.forEach((st) => {
      const d = new Date(st.created_at * 1000);
      const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      lines.push(
        `${cell(st.settlement_id)},${cell(st.utr ?? '')},${toMajor(st.amount)},${toMajor(st.fees)},${toMajor(st.tax)},${cell(st.status)},${date},${currency}`,
      );
    });
  }
  return lines.join('\n');
}

export function ReconciliationDashboardPage() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [from, setFrom] = useState(isoDaysAgo(6));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [summary, setSummary] = useState<ReconciliationSummary | null>(null);
  const [variance, setVariance] = useState<VarianceRow[]>([]);
  const [settlements, setSettlements] = useState<GatewaySettlement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, v, st] = await Promise.all([
          paymentsService.reconciliationSummary(from, to),
          paymentsService.reconciliationVariance(from, to),
          paymentsService.reconciliationSettlements(from, to).catch(() => []),
        ]);
        if (cancelled) return;
        setSummary(s);
        setVariance(v);
        setSettlements(st);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.message : 'Failed to load reconciliation';
        toast({ variant: 'error', title: 'Reconciliation failed', description: msg });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [from, to, toast]);

  const maxDaily = useMemo(() => {
    if (!summary) return 0;
    return summary.daily.reduce((m, r) => Math.max(m, r.captured + r.refunded), 0);
  }, [summary]);

  const exportCsv = () => {
    if (!summary) return;
    const blob = new Blob([buildReconciliationCsv(summary.daily, currency, variance, settlements)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reconciliation_${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ErrorBoundary>
      <PageContainer>
        {/* Hero */}
        <div className="bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-lg mb-6 animate-fade-in">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-brand-100 mb-1">Reports</div>
              <h1 className="text-2xl font-bold text-white">Payment Reconciliation</h1>
              <p className="text-brand-100 mt-1 text-sm max-w-xl">
                Captured vs refunded vs pending Razorpay transactions, with daily / method drill-downs.
              </p>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <DateField label="From" value={from} onChange={setFrom} />
              <DateField label="To" value={to} onChange={setTo} />
              <Button variant="secondary" onClick={exportCsv} disabled={!summary}>Export CSV</Button>
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            tone="brand"
            label="Captured"
            value={loading ? '—' : formatRupees(summary?.totals.captured_paise || 0, currency)}
            sub={`${summary?.totals.bill_count || 0} bills`}
            icon="✓"
            iconClasses="from-success-500 to-teal-500"
          />
          <KpiCard
            tone="warning"
            label="Refunded"
            value={loading ? '—' : formatRupees(summary?.totals.refunded_paise || 0, currency)}
            sub="Processed"
            icon="↩"
            iconClasses="from-warning-500 to-amber-500"
          />
          <KpiCard
            tone="violet"
            label="Net"
            value={loading ? '—' : formatRupees(summary?.totals.net_paise || 0, currency)}
            sub={`Captured − Refunded`}
            icon="Σ"
            iconClasses="from-violet-500 to-indigo-500"
          />
          <KpiCard
            tone="danger"
            label="Variance"
            value={loading ? '—' : `${variance.length}`}
            sub="Completed bills w/o capture"
            icon="!"
            iconClasses="from-danger-500 to-warning-500"
          />
        </div>

        <OfflinePaymentsCallout />

        {/* Daily chart */}
        <div className={cn(GLASS, 'p-6 mb-6 animate-slide-up')}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Daily</h2>
            <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-brand-500" />Captured</span>
              <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-warning-500" />Refunded</span>
            </div>
          </div>
          {loading ? (
            <Skeleton className="h-48" />
          ) : summary && summary.daily.length > 0 ? (
            <div className="space-y-2">
              {summary.daily.map((row) => (
                <DailyBar key={row.date} row={row} maxTotal={maxDaily} currency={currency} />
              ))}
            </div>
          ) : (
            <EmptyState label="No payments in this range" />
          )}
        </div>

        {/* By method */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className={cn(GLASS, 'p-6 animate-slide-up')}>
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">By Method</h2>
            {loading ? (
              <Skeleton className="h-32" />
            ) : summary && summary.by_method.length > 0 ? (
              <div className="space-y-3">
                {summary.by_method.map((m) => (
                  <div key={m.method} className="flex items-center justify-between p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-100 dark:border-neutral-700 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-brand-500 text-white flex items-center justify-center text-sm font-bold uppercase">
                        {m.method.slice(0, 2)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-neutral-900 dark:text-white capitalize">{m.method}</div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{m.bill_count} bills</div>
                      </div>
                    </div>
                    <div className="text-lg font-bold tabular-nums text-neutral-900 dark:text-white">{formatRupees(m.captured, currency)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState label="No method breakdown" />
            )}
          </div>

          {/* Variance list */}
          <div className={cn(GLASS, 'p-6 animate-slide-up')}>
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">
              Variance <span className="normal-case text-neutral-400">(bills completed without captured payment)</span>
            </h2>
            {loading ? (
              <Skeleton className="h-32" />
            ) : variance.length > 0 ? (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                      <th className="py-2 px-2">Bill</th>
                      <th className="py-2 px-2 text-right">Total</th>
                      <th className="py-2 px-2 text-right">Captured</th>
                      <th className="py-2 px-2 text-right">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variance.slice(0, 10).map((v) => (
                      <tr key={v.bill_id} className="border-b border-neutral-100 dark:border-neutral-800 hover:bg-warning-50 dark:hover:bg-warning-900/10 transition-colors">
                        <td className="py-2 px-2 font-mono text-xs">{v.bill_number}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{formatRupees(v.grand_total, currency)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{formatRupees(v.captured, currency)}</td>
                        <td className="py-2 px-2 text-right tabular-nums font-semibold text-danger-600 dark:text-danger-400">{formatRupees(v.variance, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState label="All bills reconcile cleanly 🎉" />
            )}
          </div>
        </div>

        {/* Settlements */}
        <div className={cn(GLASS, 'p-6 animate-slide-up')}>
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-4">Razorpay Settlements</h2>
          {loading ? (
            <Skeleton className="h-20" />
          ) : settlements.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                    <th className="py-2 px-2">Settlement ID</th>
                    <th className="py-2 px-2">UTR</th>
                    <th className="py-2 px-2 text-right">Amount</th>
                    <th className="py-2 px-2 text-right">Fees</th>
                    <th className="py-2 px-2 text-right">Tax</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s) => (
                    <tr key={s.settlement_id} className="border-b border-neutral-100 dark:border-neutral-800 hover:bg-brand-50 dark:hover:bg-brand-900/10 transition-colors">
                      <td className="py-2 px-2 font-mono text-xs">{s.settlement_id}</td>
                      <td className="py-2 px-2 font-mono text-xs">{s.utr || '—'}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatRupees(s.amount, currency)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatRupees(s.fees, currency)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{formatRupees(s.tax, currency)}</td>
                      <td className="py-2 px-2"><span className="inline-flex px-2 py-0.5 rounded-full bg-success-50 text-success-700 text-xs">{s.status}</span></td>
                      <td className="py-2 px-2 text-xs">{new Date(s.created_at * 1000).toLocaleDateString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState label="No settlements in this range — check your Razorpay dashboard" />
          )}
        </div>
      </PageContainer>
    </ErrorBoundary>
  );
}

/**
 * T19: card / UPI a till took offline on a manual slip waits for a manager.
 * The list lives under Sync Health (never plan-gated); this points to it.
 */
function OfflinePaymentsCallout() {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    offlinePaymentService
      .list({ status: 'UNVERIFIED', limit: 1 })
      .then((r) => {
        if (!cancelled) setCount(r.meta?.total ?? 0);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  if (!count) return null;
  return (
    <Link
      to="/sync-health/offline-payments"
      className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-warning-200 bg-gradient-to-r from-warning-50 to-amber-50 px-5 py-4 shadow-md transition-all duration-200 hover:shadow-lg animate-slide-up dark:border-warning-700/50 dark:from-warning-900/20 dark:to-amber-900/10"
    >
      <span className="text-sm font-medium text-warning-800 dark:text-warning-200">
        {count} card / UPI bill{count === 1 ? '' : 's'} paid offline — unverified. Check the slips and mark them verified.
      </span>
      <span className="rounded-full bg-warning-500 px-3 py-1 text-xs font-semibold text-white">Review</span>
    </Link>
  );
}

function DateField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col text-xs">
      <span className="text-brand-100 mb-1">{label}</span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="[color-scheme:dark] bg-white/15 border border-white/40 text-white rounded-md px-2 py-1.5 text-sm focus:bg-white/25 focus:border-white outline-none"
      />
    </label>
  );
}

function KpiCard({
  tone, label, value, sub, icon, iconClasses,
}: { tone: string; label: string; value: string; sub: string; icon: string; iconClasses: string }) {
  void tone;
  return (
    <div className={cn(GLASS, 'p-5 hover:shadow-lg transition-shadow')}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('w-10 h-10 rounded-lg text-white flex items-center justify-center bg-gradient-to-br text-lg font-bold', iconClasses)} aria-hidden>
          {icon}
        </div>
        <span className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">{label}</span>
      </div>
      <div className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">{value}</div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{sub}</div>
    </div>
  );
}

function DailyBar({ row, maxTotal, currency }: { row: { date: string; captured: number; refunded: number; bill_count: number }; maxTotal: number; currency: CurrencyCode }) {
  const capturedPct = maxTotal > 0 ? (row.captured / maxTotal) * 100 : 0;
  const refundedPct = maxTotal > 0 ? (row.refunded / maxTotal) * 100 : 0;
  const symbol = getCurrencySymbol(currency);
  return (
    <div className="grid grid-cols-[80px_1fr_120px] items-center gap-3">
      <div className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">{row.date}</div>
      <div className="relative h-6 bg-neutral-100 dark:bg-neutral-800 rounded-md overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-500 to-indigo-500 transition-[width] duration-500"
          style={{ width: `${capturedPct}%` }}
        />
        <div
          className="absolute inset-y-0 bg-gradient-to-r from-warning-500 to-amber-500 opacity-90 transition-[left,width] duration-500"
          style={{ left: `${capturedPct}%`, width: `${refundedPct}%` }}
        />
      </div>
      <div className="text-xs tabular-nums text-right text-neutral-700 dark:text-neutral-300">
        {`${symbol}${((row.captured - row.refunded) / getSubunitMultiplier(currency)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
      </div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="text-center py-8">
      <div className="text-4xl mb-2" aria-hidden>📊</div>
      <div className="text-sm text-neutral-500 dark:text-neutral-400">{label}</div>
    </div>
  );
}
